import {
  BASIS_WEIGHT_IMPERIAL_ABBREV,
  BASIS_WEIGHT_METRIC_ABBREV,
  MeasurementUnitEnum,
  MeasureType,
  MEASURE_SPECS,
  BASE_DECIMALS,
  DERIVED_IMPERIAL_DECIMALS,
  DERIVED_SMALL_IMPERIAL_DECIMALS,
  MAX_TYPED_DECIMALS,
  METRIC_DISPLAY_DECIMALS,
  TYPED_LENGTH_DECIMALS,
} from 'domains/measurementDomain';

/**
 * Conversion between the imperial values the API stores and what the user sees.
 *
 * Two rules hold everything else together:
 *
 * 1. Imperial values pass through untouched. They are already the stored value, so there is nothing
 *    to convert, and rounding them would break quantities that are legitimately finer than two
 *    decimals - a 0.0075" caliper would render as 0.01".
 * 2. Rounding happens on the way out only. `toImperial` keeps full precision, because rounding on
 *    the way back in is what turns a round trip through an edit form into silent data corruption.
 *
 * The backend twin is `MeasurementConverter.java`; the factors and the 2-decimal rule are
 * duplicated deliberately so neither side can drift without the other noticing.
 */

export const isMetric = (unit: MeasurementUnitEnum) =>
  unit === MeasurementUnitEnum.METRIC;

/** The symbol for a quantity in the given system, e.g. `mm`, `"`, `kg`. */
export const unitSymbol = (type: MeasureType, unit: MeasurementUnitEnum) => {
  const spec = MEASURE_SPECS[type];
  return isMetric(unit) ? spec.metricSymbol : spec.imperialSymbol;
};

/**
 * Converts a stored imperial value for display.
 *
 * Returns `undefined` for a null/undefined/non-finite input so callers can render a placeholder
 * rather than `NaN`.
 */
export const toDisplay = (
  imperialValue: number | string | null | undefined,
  type: MeasureType,
  unit: MeasurementUnitEnum
): number | undefined => {
  if (imperialValue === null || imperialValue === undefined || imperialValue === '') {
    return undefined;
  }
  const value = Number(imperialValue);
  if (!Number.isFinite(value)) {
    return undefined;
  }
  if (!isMetric(unit)) {
    return value;
  }
  const converted = value * MEASURE_SPECS[type].factor;
  return Number(converted.toFixed(METRIC_DISPLAY_DECIMALS));
};

/**
 * Converts a value the user typed back into the imperial unit the API stores, at full precision.
 *
 * Callers must only use this for a field the user actually edited. Round-tripping an untouched
 * field through `toDisplay` and back loses whatever the 2-decimal display rounding dropped.
 */
export const toImperial = (
  displayValue: number | string | null | undefined,
  type: MeasureType,
  unit: MeasurementUnitEnum
): number | undefined => {
  if (displayValue === null || displayValue === undefined || displayValue === '') {
    return undefined;
  }
  const value = Number(displayValue);
  if (!Number.isFinite(value)) {
    return undefined;
  }
  if (!isMetric(unit)) {
    return value;
  }
  return value / MEASURE_SPECS[type].factor;
};

/** Converts and appends the symbol, e.g. `215.9mm`. Returns `''` when there is no value. */
export const formatMeasure = (
  imperialValue: number | string | null | undefined,
  type: MeasureType,
  unit: MeasurementUnitEnum,
  options?: { withSymbol?: boolean }
): string => {
  const converted = toDisplay(imperialValue, type, unit);
  if (converted === undefined) {
    return '';
  }
  const withSymbol = options?.withSymbol ?? true;
  return withSymbol ? `${converted}${unitSymbol(type, unit)}` : `${converted}`;
};

/**
 * Imperial field suffix -> the metric companion's suffix, as the API names them.
 *
 * The API sends every measurement twice - `widthIn` next to `widthMm` - so a form can be filled
 * in whichever unit is being viewed and refilled when that changes.
 */
const METRIC_SUFFIX: [string, string][] = [
  ['CubicFt', 'CubicM'],
  ['SqIn', 'SqCm'],
  ['In', 'Mm'],
  ['Lb', 'Kg'],
];

/** Caliper is stored in inches but shown in microns, so its companion is named `...Um`. */
const MICRON_FIELDS = /caliper|thickness/i;

/**
 * The metric companion's field name for an imperial one, or undefined when it is not a
 * measurement field.
 */
export const metricFieldFor = (field: string): string | undefined => {
  for (const [imperial, metric] of METRIC_SUFFIX) {
    if (field.endsWith(imperial)) {
      const stem = field.slice(0, -imperial.length);
      return imperial === 'In' && MICRON_FIELDS.test(stem) ? `${stem}Um` : `${stem}${metric}`;
    }
  }
  return undefined;
};

/**
 * The quantity a measurement field holds, from the name of its metric companion.
 *
 * `widthIn` pairs with `widthMm` and is a length; `caliperIn` pairs with `caliperUm` and is a
 * caliper. Derived from the companion rather than from a list, so a field added to a DTO is
 * classified without anyone remembering to register it here.
 */
export const measureTypeForField = (field: string): MeasureType | undefined => {
  const companion = metricFieldFor(field);
  if (!companion) {
    return undefined;
  }
  if (companion.endsWith('Um')) {
    return MeasureType.CALIPER;
  }
  if (companion.endsWith('Kg')) {
    return MeasureType.WEIGHT;
  }
  if (companion.endsWith('SqCm')) {
    return MeasureType.AREA;
  }
  if (companion.endsWith('CubicM')) {
    return MeasureType.VOLUME;
  }
  return MeasureType.LENGTH;
};

/**
 * A record with its measurement fields filled from whichever unit is being viewed.
 *
 * The keys do not change - a form bound to `widthIn` stays bound to `widthIn` - only the value
 * swaps to the metric companion the API sent alongside it. Nothing is converted here.
 *
 * Fields with no companion in the record are left exactly as they are, so this is safe to run
 * over a whole DTO.
 */
export const inViewerUnit = <T extends Record<string, unknown>>(
  record: T | undefined | null,
  unit: MeasurementUnitEnum
): T | undefined => {
  if (!record) {
    return undefined;
  }
  if (!isMetric(unit)) {
    return record;
  }
  const shown: Record<string, unknown> = { ...record };
  Object.keys(record).forEach((field) => {
    const companion = metricFieldFor(field);
    if (companion && companion in record && record[companion] !== null
      && record[companion] !== undefined) {
      shown[field] = record[companion];
    }
  });
  return shown as T;
};

/**
 * Rounds half away from zero, the way the API's BigDecimal does.
 *
 * `toFixed` rounds the binary double, not the decimal it looks like: 0.125in is 3.175mm, which is
 * really 3.17499999... in binary, so `toFixed(2)` gives 3.17 where the API gives 3.18. A form and
 * the value it saves must not disagree by a hundredth.
 */
const roundHalfUp = (value: number, decimals: number): number => {
  const shifted = Number(`${value}e${decimals}`);
  const rounded = Math.sign(shifted) * Math.round(Math.abs(shifted));
  return Number(`${rounded}e-${decimals}`);
};

/**
 * Re-expresses a number the user has already typed, in a different unit.
 *
 * Only for values sitting in an open form when the viewer switches units. Everything the API
 * sends arrives in both units and is picked, never converted - but a number the user typed a
 * moment ago exists in one unit only, and leaving it under the new unit's label is what wrote
 * metric values into imperial columns.
 *
 * Rounded to the precision of the unit it is going into, not to one shared precision: imperial is
 * the base system, so it keeps the six decimals the API stores, and metric keeps the three the
 * forms accept. Rounding the base side to what reads nicely is what turned an entered 888mm into
 * 887.98 on the way back.
 */
export const reexpress = (
  value: number | string | null | undefined,
  type: MeasureType,
  from: MeasurementUnitEnum,
  to: MeasurementUnitEnum
): number | undefined => {
  if (value === null || value === undefined || value === '' || from === to) {
    return value === '' || value === null || value === undefined
      ? undefined
      : Number(value);
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return undefined;
  }
  const { factor } = MEASURE_SPECS[type];
  const converted = isMetric(to) ? numeric * factor : numeric / factor;
  return roundHalfUp(converted, isMetric(to) ? METRIC_DISPLAY_DECIMALS : BASE_DECIMALS);
};

/**
 * Rounds a numeric measurement the API already returned in the viewer's unit.
 *
 * Presentation only - the API converts every numeric measurement at its own boundary, so the
 * number that arrives is already the one to show. Only the derived side ever carries a long tail.
 */
export const toShown = (
  value: number | string | null | undefined,
  metric = false
): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return undefined;
  }
  if (metric) {
    return Number(numeric.toFixed(METRIC_DISPLAY_DECIMALS));
  }
  // An imperial value the supplier typed is shown exactly - rounding it turned a 0.0075in
  // caliper into 0.008in and an 8.125in trim into 8.13in, neither of which is the stock anyone
  // ordered. One that was *converted* from a metric entry is a different thing: it carries the
  // six decimals the base system keeps, and nobody wants to read them.
  return decimalsOf(numeric) > MAX_TYPED_DECIMALS
    ? Number(numeric.toFixed(METRIC_DISPLAY_DECIMALS))
    : numeric;
};

/**
 * How many decimals a number actually carries.
 *
 * Off its own decimal text rather than off the stored string, so it does not matter whether the
 * value arrived as `8.125` or `"8.1250"`.
 */
const decimalsOf = (value: number): number => {
  const text = String(value);
  if (text.includes('e') || text.includes('E')) {
    // Exponential notation only shows up for values far outside anything measured here, and
    // counting its decimals is meaningless. Treat it as derived.
    return Number.MAX_SAFE_INTEGER;
  }
  return text.split('.')[1]?.length ?? 0;
};

/**
 * Formats a value the API already returned in the viewer's unit.
 *
 * Rounds and optionally adds the symbol, but never scales. Use this for anything served by an
 * endpoint that converts on its side - converting again is what turns 8.5in into 5483.86mm.
 */
export const formatConvertedMeasure = (
  value: number | string | null | undefined,
  type: MeasureType,
  unit: MeasurementUnitEnum,
  options?: { withSymbol?: boolean }
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '';
  }
  // Only the derived side ever carries a long tail, so rounding here is presentation only.
  const shown = Number(numeric.toFixed(METRIC_DISPLAY_DECIMALS));
  const withSymbol = options?.withSymbol ?? true;
  return withSymbol ? `${shown}${unitSymbol(type, unit)}` : `${shown}`;
};

/** The spelled unit for a label or column header, e.g. `in`, `mm`, `kg`. */
export const unitAbbrev = (type: MeasureType, unit: MeasurementUnitEnum) => {
  const spec = MEASURE_SPECS[type];
  return isMetric(unit) ? spec.metricAbbrev : spec.imperialAbbrev;
};

const SIZE_SEPARATORS = /[xX×]/;

/**
 * Decimals a metric size axis is shown to.
 *
 * Three, because that is what the forms accept, and a screen that cannot show back what was typed
 * defeats storing a single base value however precisely it is stored: an entered 215.875mm shown
 * to two decimals reads 215.88. The API sends metric to three decimals for the same reason.
 */
const METRIC_SIZE_DISPLAY_DECIMALS = 3;

/**
 * Shortens a size for display, without changing what it means.
 *
 * The side of a size nobody typed is derived to six decimals, so an A4 picked in millimetres
 * reads `8.267717x11.692913` in inches - a precision the pricing engine needs and a reader does
 * not. This is display only; the stored string keeps every digit, and is what selections are
 * matched on and posted back.
 *
 * Trailing zeros are dropped rather than padded, so a size that was typed - `8.5x11` - still
 * reads the way its supplier wrote it.
 *
 * An axis that is not a number is left exactly as it is: these are supplier free text, and one
 * that never parsed should not start disappearing now.
 */
/** One axis of a size, at the precision a size is read to. See {@link roundSizeForDisplay}. */
export const roundSizeAxis = (
  value: number | string | null | undefined,
  metric = false
) => {
  const numeric = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(numeric)) {
    return undefined;
  }
  if (metric) {
    return Number(numeric.toFixed(METRIC_SIZE_DISPLAY_DECIMALS));
  }
  // A size the supplier typed is shown exactly as typed: rounding to two places read an 8.125in
  // trim back as 8.13in, a size nobody offered. But an A4 picked in millimetres is stored as the
  // 8.267717in it converts to, and six decimals is not a size either - it is the base system
  // showing through. The form caps what can be typed at three decimals, so anything longer was
  // converted rather than entered, and only those are rounded.
  if (decimalsOf(numeric) <= TYPED_LENGTH_DECIMALS) {
    return numeric;
  }
  return Number(
    numeric.toFixed(
      Math.abs(numeric) < 1
        ? DERIVED_SMALL_IMPERIAL_DECIMALS
        : DERIVED_IMPERIAL_DECIMALS
    )
  );
};

export const roundSizeForDisplay = (
  size: string | null | undefined,
  metric = false
): string | undefined => {
  if (!size) {
    return size ?? undefined;
  }
  const separator = size.match(SIZE_SEPARATORS)?.[0] ?? 'x';
  return size
    .split(SIZE_SEPARATORS)
    .map((axis) => {
      const trimmed = axis.trim();
      const numeric = Number(trimmed);
      if (trimmed === '' || !Number.isFinite(numeric)) {
        return trimmed;
      }
      return String(roundSizeAxis(numeric, metric));
    })
    .join(separator);
};

/**
 * Splits a stored size string such as `8.5x11` or `10x8x4` into its numeric parts.
 *
 * Returns `null` when the string is not a size, which is the caller's cue to render it verbatim -
 * these are user-entered strings and one that never parsed cleanly should not start throwing now.
 */
export const parseSize = (size: string | null | undefined): number[] | null => {
  if (!size) {
    return null;
  }
  const parts = size.split(SIZE_SEPARATORS);
  if (parts.length < 2) {
    return null;
  }
  const numbers = parts.map((part) => Number(part.trim()));
  return numbers.some((n) => !Number.isFinite(n)) ? null : numbers;
};

/**
 * Converts a size string for display, keeping the `x` separated shape.
 *
 * The result is for rendering only. It must never be fed back as a form value or sent to the API -
 * those still expect the original imperial string.
 */
export const convertSize = (
  size: string | null | undefined,
  unit: MeasurementUnitEnum
): string => {
  if (!size) {
    return '';
  }
  if (!isMetric(unit)) {
    return size;
  }
  const parts = parseSize(size);
  if (!parts) {
    return size;
  }
  return parts
    .map((part) => toDisplay(part, MeasureType.LENGTH, unit))
    .join('x');
};

/**
 * Converts a size string back to the imperial string the API stores, at full precision.
 *
 * Only for sizes the user actually edited - see `toImperial`.
 */
export const sizeToImperial = (
  size: string | null | undefined,
  unit: MeasurementUnitEnum
): string => {
  if (!size) {
    return '';
  }
  if (!isMetric(unit)) {
    return size;
  }
  const parts = parseSize(size);
  if (!parts) {
    return size;
  }
  return parts
    .map((part) => toImperial(part, MeasureType.LENGTH, unit))
    .join('x');
};

/** Where the user's own choice is remembered between sessions. */
const MEASUREMENT_UNIT_STORAGE_KEY = 'measurementUnit';

/**
 * The unit this person picked for themselves, or undefined when they never have.
 *
 * Their choice, not the org's default, and it outranks it: the org setting answers "which system
 * is this org's data in", and this answers "which one do I want to read in". Someone who works
 * across a US and a Japanese supplier picks once and keeps it.
 *
 * Storage access is guarded because this runs at module load: a private window, blocked site
 * data, or any non-browser context makes `localStorage` throw or be absent, and the whole module
 * would fail to import.
 */
export const readStoredUnit = (): MeasurementUnitEnum | undefined => {
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(MEASUREMENT_UNIT_STORAGE_KEY);
  } catch {
    return undefined;
  }
  return stored === MeasurementUnitEnum.METRIC ||
    stored === MeasurementUnitEnum.IMPERIAL
    ? (stored as MeasurementUnitEnum)
    : undefined;
};

/** Remembers the choice where possible; the switch still applies to this session either way. */
export const storeUnit = (unit: MeasurementUnitEnum) => {
  try {
    localStorage.setItem(MEASUREMENT_UNIT_STORAGE_KEY, unit);
  } catch {
    // Not remembering the choice is better than failing the switch.
  }
};

/**
 * The unit server-rendered output is asked for.
 *
 * Held here rather than read from the redux store so that `utils/api` - which every model imports -
 * does not have to import a model back. The app keeps it in step: seeded from the user's own
 * remembered choice at load, replaced by the org's default when they have never chosen, and
 * updated on each switch.
 */
let effectiveUnit: MeasurementUnitEnum =
  readStoredUnit() ?? MeasurementUnitEnum.IMPERIAL;

/** Where the org's last resolved default is cached, per org. */
const UNIT_CACHE_KEY = 'measurementUnit.byOrg';

/**
 * The unit this org resolved to last time, so a reload does not start in the wrong one.
 *
 * Not a preference - a cache of the server's answer, keyed by org. The page used to render in
 * imperial and flip to metric when the org call came back, which is a visible wrong number on
 * every load for every metric org, and a form opened in that window was filled against a label
 * that then changed.
 *
 * Keyed by org because one person can belong to several, and the cached answer for one says
 * nothing about another.
 */
export const cachedUnitForOrg = (
  orgId: number | undefined
): MeasurementUnitEnum | undefined => {
  if (!orgId) {
    return undefined;
  }
  try {
    const cache = JSON.parse(localStorage.getItem(UNIT_CACHE_KEY) ?? '{}');
    const cached = cache?.[String(orgId)];
    return cached === MeasurementUnitEnum.METRIC ||
      cached === MeasurementUnitEnum.IMPERIAL
      ? cached
      : undefined;
  } catch {
    // A private window, blocked site data, or a value someone hand-edited. The server is about
    // to answer anyway; this only decides what is on screen until it does.
    return undefined;
  }
};

export const cacheUnitForOrg = (
  orgId: number | undefined,
  unit: MeasurementUnitEnum
) => {
  if (!orgId) {
    return;
  }
  try {
    const cache = JSON.parse(localStorage.getItem(UNIT_CACHE_KEY) ?? '{}');
    cache[String(orgId)] = unit;
    localStorage.setItem(UNIT_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Not caching it costs one flip on the next load, which is what this is here to avoid, not
    // something worth failing over.
  }
};

export const getEffectiveUnit = () => effectiveUnit;

export const setEffectiveUnit = (unit: MeasurementUnitEnum) => {
  effectiveUnit = unit;
};

/** `lb` or `gsm`, for a label or column header. */
export const basisWeightAbbrev = (unit: MeasurementUnitEnum) =>
  isMetric(unit) ? BASIS_WEIGHT_METRIC_ABBREV : BASIS_WEIGHT_IMPERIAL_ABBREV;
